import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Careers", path: "/careers" },
    { name: "Apply Now", path: "/apply" },
    { name: "Contact", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Calculate scroll progress
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* Scroll Progress Bar */}
      <div 
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-[#6DB432] via-[#8BC064] to-[#6DB432] z-[100] transition-all duration-150"
        style={{ width: `${scrollProgress}%` }}
      />
      
      <nav
        data-testid="navbar"
        className={`fixed top-1 left-0 right-0 z-[90] transition-all duration-500 ${
          scrolled || isOpen
            ? "bg-[#353537]/95 backdrop-blur-md shadow-lg shadow-black/20"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link
              to="/"
              data-testid="logo-link"
              className="flex items-center group relative z-[95]"
            >
              <img
                src="/images/logo-dark.png"
                alt="NUVEXA - Powered by PRECISION"
                className="h-14 md:h-20 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(" ", "-")}`}
                  className={`relative text-sm font-medium uppercase tracking-wider transition-all duration-300 group magnetic-hover ${
                    location.pathname === link.path
                      ? "text-[#6DB432]"
                      : "text-gray-300 hover:text-[#6DB432]"
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-[#6DB432] to-[#8BC064] transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href="tel:+923356883921"
                data-testid="nav-phone-btn"
                className="flex items-center gap-2 text-gray-300 hover:text-[#6DB432] transition-all duration-300 group shake-hover"
              >
                <Phone className="w-4 h-4 group-hover:animate-pulse" />
                <span className="text-sm font-medium">+92 335 688 3921</span>
              </a>
              <Link to="/contact">
                <Button
                  data-testid="nav-get-started-btn"
                  className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-6 py-2 rounded-xl btn-glow btn-shine group flex items-center gap-2 elastic-scale"
                >
                  <Sparkles className="w-4 h-4 group-hover:animate-spin" style={{ animationDuration: '2s' }} />
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors duration-300 relative z-[95]"
              onClick={() => setIsOpen(!isOpen)}
            >
              <div className="relative w-6 h-6">
                <Menu className={`w-6 h-6 absolute transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} />
                <X className={`w-6 h-6 absolute transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Separate from nav for proper z-index */}
      <div
        className={`lg:hidden fixed inset-0 z-[85] transition-all duration-500 ${
          isOpen ? "visible" : "invisible"
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-[#1a1a1b]/95 backdrop-blur-xl transition-opacity duration-500 ${
            isOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsOpen(false)}
        />
        
        {/* Menu Content */}
        <div 
          className={`absolute inset-0 flex flex-col items-center justify-center gap-6 p-8 transition-all duration-500 ${
            isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        >
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(" ", "-")}`}
              className={`text-2xl font-bold uppercase tracking-wider transition-all duration-300 transform ${
                location.pathname === link.path
                  ? "text-[#6DB432] scale-110"
                  : "text-white hover:text-[#6DB432] hover:scale-105"
              }`}
              style={{ 
                transitionDelay: isOpen ? `${index * 50}ms` : '0ms'
              }}
              onClick={() => setIsOpen(false)}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/contact" className="mt-6" onClick={() => setIsOpen(false)}>
            <Button
              data-testid="mobile-get-started-btn"
              className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-8 py-3 rounded-md btn-glow btn-shine text-lg"
            >
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navbar;
